/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import { useState } from "react";
import { Search, Hash, FileText, AlertCircle, Loader2, CheckCircle } from "lucide-react";

export default function NetSuitePOSearch() {
  const [poNumber, setPoNumber] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FLUXONIX BRANDING (inline styles)
  // ============================================================

  const colors = {
    primary: "#5B4BFF",
    primaryDark: "#4338CA",
    secondary: "#8B5CF6",
    gradient: "linear-gradient(135deg, #5B4BFF, #8B5CF6)",
    bgSoft: "#F7F7FC",
    textDark: "#111827",
    textSoft: "#6B7280",
  };

  const fx = {
    page: {
      minHeight: "100vh",
      padding: "50px 20px",
      background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
      fontFamily: "Inter, sans-serif",
    },
    card: {
      maxWidth: "950px",
      margin: "0 auto",
      background: "#fff",
      padding: "45px",
      borderRadius: "24px",
      boxShadow: "0 15px 40px rgba(80, 70, 200, 0.15)",
    },
    h1: {
      fontSize: "32px",
      fontWeight: 800,
      color: colors.textDark,
      marginBottom: "6px",
    },
    subtitle: {
      fontSize: "16px",
      color: colors.textSoft,
      marginBottom: "35px",
    },
    steps: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "45px",
    },
    step: {
      textAlign: "center",
      width: "33.33%",
    },
    stepCircle: (active, completed) => ({
      width: "54px",
      height: "54px",
      borderRadius: "50%",
      background: completed
        ? "#4ADE80"
        : active
        ? colors.primary
        : "#CBD5E1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      margin: "0 auto 10px",
      transition: "0.3s",
    }),
    stepLabel: (active, completed) => ({
      fontSize: "13px",
      fontWeight: 600,
      color: active || completed ? colors.textDark : "#9CA3AF",
    }),
    inputGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: 600,
      color: colors.textDark,
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      fontSize: "15px",
      borderRadius: "12px",
      border: "2px solid #E5E7EB",
      outline: "none",
      transition: "0.2s",
      boxSizing: "border-box",
    },
    button: {
      width: "100%",
      padding: "15px",
      fontSize: "16px",
      fontWeight: 700,
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      color: "#fff",
      background: colors.gradient,
      marginTop: "16px",
      boxShadow: "0 8px 20px rgba(91,75,255,0.35)",
      transition: "0.25s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    buttonDisabled: {
      width: "100%",
      padding: "15px",
      fontSize: "16px",
      fontWeight: 700,
      borderRadius: "12px",
      border: "none",
      cursor: "not-allowed",
      color: "#fff",
      background: "#CBD5E1",
      marginTop: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    errorBox: {
      background: "#FEF2F2",
      border: "1px solid #FECACA",
      borderRadius: "12px",
      padding: "16px",
      marginTop: "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    resultBox: {
      background: colors.bgSoft,
      padding: "25px",
      borderRadius: "16px",
      marginTop: "30px",
      border: "1px solid #E5E7EB",
      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    },
    resultHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
    },
    resultTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: colors.textDark,
    },
    resultContent: {
      background: "#fff",
      padding: "20px",
      borderRadius: "12px",
      fontSize: "14px",
      fontFamily: "monospace",
      color: colors.textDark,
      whiteSpace: "pre-wrap",
      maxHeight: "400px",
      overflowY: "auto",
      border: "1px solid #E5E7EB",
    },
    rowButtons: {
      display: "flex",
      gap: "12px",
      marginTop: "20px",
    },
    btnGreen: {
      flex: 1,
      padding: "15px",
      borderRadius: "12px",
      border: "none",
      background: "#22C55E",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    btnGray: {
      flex: 1,
      padding: "15px",
      borderRadius: "12px",
      border: "none",
      background: "#6B7280",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  // ============================================================
  // STEP COMPONENT
  // ============================================================

  const Step = ({ icon: Icon, label, active, completed }) => (
    <div style={fx.step}>
      <div style={fx.stepCircle(active, completed)}>
        {completed ? <CheckCircle size={28} /> : <Icon size={28} />}
      </div>
      <div style={fx.stepLabel(active, completed)}>{label}</div>
    </div>
  );

  // ============================================================
  // API CALL
  // ============================================================

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const res = await fetch("/api/admin/connectors/netsuite/po/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ po_number: poNumber }),
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to fetch");
    }

    setLoading(false);
  };

  const handleReset = () => {
    setPoNumber("");
    setResult(null);
    setError("");
  };

  const canSearch = poNumber.trim() !== "";

  // ============================================================
  // RENDER UI
  // ============================================================

  return (
    <div style={fx.page}>
      <div style={fx.card}>
        <h1 style={fx.h1}>NetSuite PO Search</h1>
        <p style={fx.subtitle}>
          Search and retrieve Purchase Orders from NetSuite by PO number.
        </p>

        {/* Steps */}
        <div style={fx.steps}>
          <Step
            icon={Hash}
            label="Enter PO Number"
            active={!result && !loading}
            completed={canSearch && (loading || result)}
          />
          <Step
            icon={Search}
            label="Search"
            active={loading}
            completed={result !== null}
          />
          <Step
            icon={FileText}
            label="Results"
            active={result !== null}
            completed={false}
          />
        </div>

        {/* PO Number Input */}
        <div style={fx.inputGroup}>
          <label style={fx.label}>PO Number</label>
          <input
            type="text"
            placeholder="Enter Purchase Order number..."
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canSearch && !loading && handleSearch()}
            style={fx.input}
          />
        </div>

        {/* Search Button */}
        <button
          style={canSearch && !loading ? fx.button : fx.buttonDisabled}
          onClick={handleSearch}
          disabled={!canSearch || loading}
        >
          {loading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search size={20} />
              Search Purchase Order
            </>
          )}
        </button>

        {/* Error */}
        {error && (
          <div style={fx.errorBox}>
            <AlertCircle size={22} color="#EF4444" />
            <span style={{ color: "#DC2626", fontSize: "15px" }}>{error}</span>
          </div>
        )}

        {/* Results */}
        {result && (
          <div style={fx.resultBox}>
            <div style={fx.resultHeader}>
              <FileText size={22} color={colors.primary} />
              <span style={fx.resultTitle}>Search Results</span>
            </div>
            <div style={fx.resultContent}>
              {JSON.stringify(result, null, 2)}
            </div>

            <div style={fx.rowButtons}>
              <button
                style={fx.btnGreen}
                onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))}
              >
                Copy JSON
              </button>
              <button style={fx.btnGray} onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
